import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ChevronRight, Close } from '@mui/icons-material'
import { Box, Button, Divider, Grid, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'

import ScreenContainer from '../../components/screen'
import Api from '../../utils/api'

import ActivationInstructions from './activation-instructions'
import { FinalizeDialog, HelpDialog, LocateCodeDialog } from './dialogs'
import { type ActivatableDevice, NODE_TYPE_LEAK5S, NODE_TYPE_NBIOT_LEAK, sortDevicesByTypePriority } from './helpers'
import classes from './index.styles'

const DeviceRoster = () => {
  const navigate = useNavigate()
  const { folderId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const [loading, setLoading] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [showFinalizeDialog, setShowFinalizeDialog] = useState<boolean>(false)
  const [showHelpDialog, setShowHelpDialog] = useState<boolean>(false)
  const [showLocateCodeDialog, setShowLocateCodeDialog] = useState<boolean>(false)
  const [hasActivatedDevices, setHasActivatedDevices] = useState<boolean>(false)
  const [activatableDevices, setActivatableDevices] = useState<ActivatableDevice[]>([])

  useEffect(() => {
    const fetchData = async () => {
      if (!folderId) return
      setLoading(true)
      try {
        const deviceActivationCheckRes = await Api.get(`/api/v2/protect/folders/${folderId}/device-activation-check`)
        const devicesRes = await Api.get(`/api/v2/protect/folders/${folderId}/devices`)

        if (!deviceActivationCheckRes.ok || !devicesRes.ok) throw new Error() // Throw error to the catch block
        const deviceActivationSeats: ICanActivateDeviceResponse[] = deviceActivationCheckRes.body?.devices ?? []
        const installedCount = deviceActivationSeats.reduce((sum, d) => sum + d.totalDevicesInstalled, 0)
        setHasActivatedDevices(installedCount > 0)

        const notActivatedFromRes: IDeviceType[] = devicesRes.body.devices.filter(
          (device: IDeviceType) =>
            device.node.isActive && !device.isActivated && (device.node.nodeTypeName === NODE_TYPE_LEAK5S || device.node.nodeTypeName === NODE_TYPE_NBIOT_LEAK)
        )

        // Separate device node records by type
        const slimNodes = notActivatedFromRes
          .filter((d) => d.node.nodeTypeName === NODE_TYPE_LEAK5S)
          .map((d) => ({ displayName: d.node.vanity, nodeTypeName: NODE_TYPE_LEAK5S, nodeId: d.node.id }))

        const leakFreezeNodes = notActivatedFromRes
          .filter((d) => d.node.nodeTypeName === NODE_TYPE_NBIOT_LEAK)
          .map((d) => ({ displayName: d.node.vanity, nodeTypeName: NODE_TYPE_NBIOT_LEAK, nodeId: d.node.id }))

        // For each Slim seat, use the device node if available, otherwise leave a placeholder
        const slimSeatCount = deviceActivationSeats.filter((d) => d.nodeTypeName === NODE_TYPE_LEAK5S).reduce((sum, d) => sum + d.totalDevicesNotInstalled, 0)

        const slimEntries: ActivatableDevice[] = Array.from(
          { length: slimSeatCount },
          (_, i) => slimNodes[i] ?? { displayName: 'Defender Slim', nodeTypeName: NODE_TYPE_LEAK5S, nodeId: null }
        )

        // Any Slim devices beyond the seat count are appended
        const notActivateSlimNodes = slimNodes.slice(slimSeatCount)

        setActivatableDevices([...slimEntries, ...notActivateSlimNodes, ...leakFreezeNodes])
      } catch {
        enqueueSnackbar('Failed to fetch devices', { variant: 'error' })
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [enqueueSnackbar, folderId])

  const sortedDevices = useMemo(() => sortDevicesByTypePriority(activatableDevices), [activatableDevices])

  const totalActivatableDevices = activatableDevices.length

  const hasNbiotLeak = activatableDevices.some((d) => d.nodeTypeName === NODE_TYPE_NBIOT_LEAK)
  const hasLeak5s = activatableDevices.some((d) => d.nodeTypeName === NODE_TYPE_LEAK5S)

  const onCompleteActivation = async () => {
    if (!folderId) return
    setSubmitting(true)
    try {
      const resActivation = await Api.post(`/api/v2/folders/${folderId}/activation`, {})
      const resAttributes = await Api.patch(`/api/v2/folders/${folderId}/attributes`, {
        activationMode: 'online',
        activationApp: 'Protect Web',
      })
      if (!resActivation.ok || !resAttributes.ok) throw new Error()
      enqueueSnackbar('Installation completed successfully')
      navigate(`/dashboard/${folderId}`, { state: { showBookmarkInfo: true } })
    } catch {
      enqueueSnackbar('Could not complete installation, try again', { variant: 'error' })
    } finally {
      setShowFinalizeDialog(false)
      setSubmitting(false)
    }
  }

  return (
    <ScreenContainer
      loading={loading}
      headerProps={{
        title: 'Activate',
        rightIcon: (
          <IconButton onClick={() => navigate(`/dashboard/${folderId}`)} aria-label="Return to dashboard">
            <Close color="secondary" />
          </IconButton>
        ),
      }}
    >
      <Grid container sx={classes.mainContainer}>
        <Grid container sx={classes.headerRow}>
          <Typography variant="h6">Ready to Activate</Typography>
          <Typography variant="body2" sx={classes.deviceCount}>
            {totalActivatableDevices}
            {totalActivatableDevices !== 1 ? ' Devices' : ' Device'}
          </Typography>
        </Grid>

        <Grid>
          <ActivationInstructions hasNbiotLeak={hasNbiotLeak} hasLeak5s={hasLeak5s} onLocateCodeClick={() => setShowLocateCodeDialog(true)} />
        </Grid>

        <Divider sx={classes.divider} />

        <Grid>
          <List>
            {sortedDevices.length > 0 &&
              sortedDevices.map((device, index) => (
                <ListItem key={device.nodeId ?? `seat-${device.nodeTypeName}-${index}`} disablePadding sx={classes.listItem}>
                  <ListItemButton
                    sx={classes.listItemButton}
                    onClick={() =>
                      device.nodeTypeName === NODE_TYPE_LEAK5S
                        ? navigate('/sensor-activation')
                        : navigate('/meet-sensor', {
                            state: { nodeTypeName: device.nodeTypeName, nodeId: device.nodeId, folderId: Number(folderId), meshifyId: '', sidewalkId: '' },
                          })
                    }
                  >
                    <ListItemText primary={device.displayName} />
                    <ListItemIcon sx={classes.listItemIcon}>
                      <ChevronRight color="secondary" />
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        </Grid>
      </Grid>

      <Box sx={classes.footer}>
        <Grid container size={12} sx={classes.footerButtonsContainer(hasActivatedDevices)}>
          <Button variant="text" onClick={() => setShowHelpDialog(true)}>
            Need help?
          </Button>

          {hasActivatedDevices ? (
            <Button
              variant="contained"
              disableElevation
              color="secondary"
              disabled={submitting}
              loading={submitting}
              onClick={() => (activatableDevices.length > 0 ? setShowFinalizeDialog(true) : onCompleteActivation())}
            >
              I’m done
            </Button>
          ) : undefined}
        </Grid>
      </Box>

      <HelpDialog open={showHelpDialog} onClose={() => setShowHelpDialog(false)} />
      <LocateCodeDialog open={showLocateCodeDialog} onClose={() => setShowLocateCodeDialog(false)} />
      <FinalizeDialog open={showFinalizeDialog} onClose={() => setShowFinalizeDialog(false)} onConfirm={onCompleteActivation} />
    </ScreenContainer>
  )
}

export default DeviceRoster
